import { useCallback, useState } from 'react';

import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';

export const useEquitySellAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['equitySell'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const defaultQuantity = offer.orderQuantity || offer.saleAvailableAmount || offer.quote.lotSize;
  const defaultValue = offer.orderValue || offer.priceNormalized;

  const [isFocusedAmountInput, setIsFocusedAmountInput] = useState(false);
  const [amountItem, setAmountItem] = useState(defaultQuantity);
  const [priceItem, setPriceItem] = useState(defaultValue);

  const totalPrice = amountItem * priceItem;

  const totalPriceIsInvalid = totalPrice === 0 || totalPrice > 1000000;

  const handleChangeAmountItem = useCallback(
    async (value: number) => {
      if (submitUpdate) {
        const response = await submitUpdate({ amount: value, price: priceItem });

        setAmountItem((state) => (!totalPriceIsInvalid && response ? value : state));
      } else {
        setAmountItem(value);
      }
    },
    [priceItem, submitUpdate, totalPriceIsInvalid]
  );

  const handleChangePriceItem = useCallback(
    async (value: number) => {
      const valueFixed = Number(value.toFixed(2));

      if (submitUpdate) {
        const response = await submitUpdate({ price: valueFixed, amount: amountItem });

        setPriceItem((state) => (!totalPriceIsInvalid && response ? valueFixed : state));
      } else {
        setPriceItem(valueFixed);
      }
    },
    [amountItem, submitUpdate, totalPriceIsInvalid]
  );

  const availableAmount = offer.saleAvailableAmount || 1000000;
  const lotSize = offer.quote.lotSize || 1;
  const fractionalLotSize = offer.fractionalQuote
    ? offer.fractionalQuote.lotSize
    : offer.quote.lotSize;
  const cautionStepper =
    amountItem <= lotSize && offer.fractionalQuote ? fractionalLotSize : lotSize;

  return {
    amountItem,
    totalPrice,
    priceItem,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
    cautionStepper,
    fractionalLotSize,
    isFocusedAmountInput,
    setIsFocusedAmountInput,
    availableAmount,
  };
};

export type EquityController = ReturnType<typeof useEquitySellAssetController>;
